import React, { useEffect, useState } from "react";
import { api, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import {
  CheckSquare, Square, Plus, Trash2, Save, X, CalendarDays, User, ClipboardCheck,
  ClipboardList, MessageSquare, AlertTriangle, Filter,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";

const PERSON_CLR = {
  Pranesh:  "bg-[#DBEAFE] text-[#1D4ED8]",
  Yash:     "bg-[#DCFCE7] text-[#15803D]",
  Krutika:  "bg-[#FEF3C7] text-[#B45309]",
};

const PRIORITY_BADGE = {
  low:    "bg-gray-100 text-gray-600",
  normal: "bg-[#DBEAFE] text-[#1D4ED8]",
  high:   "bg-[#FEE2E2] text-[#B91C1C]",
};

function PendingWorks({ currentUser }) {
  const isAdmin = currentUser?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("mine"); // mine | by_me | all
  const [showDone, setShowDone] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pending-works", { params: { status: showDone ? "all" : "pending" } });
      setRows(data.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [showDone]);

  const filtered = rows.filter((r) => {
    if (filter === "mine") return r.task?.assigned_to_user_id === currentUser?.id;
    if (filter === "by_me") return r.from_user_id === currentUser?.id;
    return true;
  });

  const toggleDone = async (r) => {
    const nextStatus = r.task?.status === "done" ? "pending" : "done";
    try {
      await api.patch(`/pending-works/${r.id}`, { status: nextStatus });
      toast.success(nextStatus === "done" ? "Marked done" : "Reopened");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete task "${r.task?.text}"?`)) return;
    try {
      await api.delete(`/pending-works/${r.id}`);
      toast.success("Task deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const today = todayISO();

  return (
    <div className="card-flat" data-testid="pending-works-panel">
      <div className="p-4 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardList size={16} className="text-black" />
          <div className="font-heading font-black text-lg">Pending Works</div>
          <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
            Ad-hoc tasks from Team Chat
          </span>
        </div>
        <Link to="/chat" className="text-xs text-gray-500 hover:text-black flex items-center gap-1" data-testid="pending-works-open-chat">
          <MessageSquare size={13} /> Open chat
        </Link>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Filter size={13} className="text-gray-400" />
          {[
            { k: "mine", l: "Assigned to me" },
            { k: "by_me", l: "Sent by me" },
            ...(isAdmin ? [{ k: "all", l: "All" }] : []),
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`h-8 px-3 text-xs font-semibold border rounded-sm ${filter === f.k ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
              data-testid={`pw-filter-${f.k}`}
            >
              {f.l}
            </button>
          ))}
          <label className="text-xs text-gray-600 flex items-center gap-1.5 ml-2">
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} data-testid="pw-show-done" />
            Show done
          </label>
        </div>
      </div>

      <div className="overflow-auto">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left w-8"></th>
              <th className="text-left">Task</th>
              <th className="text-left">Priority</th>
              <th className="text-left">Assignee</th>
              <th className="text-left">From</th>
              <th className="text-left">Due</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="py-6 text-center text-xs text-gray-400">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="7" className="py-6 text-center text-xs text-gray-400">
                <ClipboardList size={22} className="inline-block text-gray-300 mb-1" />
                <div>No pending works {filter === "mine" ? "assigned to you" : filter === "by_me" ? "sent by you" : ""}.</div>
                <div className="text-[11px] mt-1">Send tasks via Team Chat to see them here.</div>
              </td></tr>
            )}
            {filtered.map((r) => {
              const t = r.task || {};
              const isDone = t.status === "done";
              const isOverdue = !isDone && t.due_date && t.due_date < today;
              return (
                <tr key={r.id} className={`${isDone ? "bg-[#F0FDF4]" : ""}`} data-testid={`pw-row-${r.id}`}>
                  <td>
                    <button onClick={() => toggleDone(r)} className="p-1" data-testid={`pw-toggle-${r.id}`}>
                      {isDone
                        ? <CheckSquare size={18} className="text-[#15803D]" strokeWidth={2.5} />
                        : <Square size={18} className="text-gray-300" strokeWidth={2} />}
                    </button>
                  </td>
                  <td className="max-w-md">
                    <div className={`text-sm ${isDone ? "line-through text-gray-500" : "font-medium"}`}>{t.text}</div>
                    {t.notes && <div className="text-[11px] text-gray-500 mt-0.5">{t.notes}</div>}
                    {isDone && t.done_by_name && (
                      <div className="text-[10px] text-[#15803D] mt-0.5">
                        ✓ by {t.done_by_name} {t.done_at ? `· ${format(parseISO(t.done_at), "HH:mm dd MMM")}` : ""}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`px-2 py-0.5 text-[11px] rounded-sm font-bold ${PRIORITY_BADGE[t.priority] || PRIORITY_BADGE.normal}`}>
                      {(t.priority || "normal").toUpperCase()}
                    </span>
                  </td>
                  <td className="text-xs">{t.assigned_to_name || "—"}</td>
                  <td className="text-xs text-gray-500">{r.from_name || "—"}</td>
                  <td className="text-xs data-num">
                    {t.due_date
                      ? <span className={isOverdue ? "text-[#B91C1C] font-bold" : ""}>{t.due_date}{isOverdue && <> <AlertTriangle size={11} className="inline" /></>}</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="text-right">
                    {(r.from_user_id === currentUser?.id || isAdmin) && (
                      <button onClick={() => remove(r)} className="text-gray-300 hover:text-[#B91C1C] p-1" title="Delete" data-testid={`pw-del-${r.id}`}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Checklist() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addForPerson, setAddForPerson] = useState(null);
  const [newTask, setNewTask] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/checklist/day", { params: { date } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [date]);

  const toggle = async (item) => {
    try {
      await api.post(`/checklist/items/${item.id}/toggle`, { date, done: !item.done });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const addTask = async () => {
    if (!newTask.trim() || !addForPerson) return;
    try {
      await api.post("/checklist/items", { person: addForPerson, task: newTask, order: 99, active: true });
      toast.success("Task added");
      setNewTask("");
      setAddForPerson(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this task permanently?")) return;
    try { await api.delete(`/checklist/items/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="checklist-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={20} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Daily Checklist</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Who is doing what today · tick as you go
        </span>
        <div className="ml-auto flex items-center gap-2">
          <CalendarDays size={14} className="text-gray-500" />
          <input type="date" className="input-lg h-9" value={date} onChange={(e) => setDate(e.target.value)} data-testid="checklist-date" />
        </div>
      </div>

      {/* Progress cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {(data?.summary || []).map((s) => (
          <div key={s.person} className="card-flat p-5" data-testid={`progress-${s.person}`}>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 text-xs rounded-sm font-semibold ${PERSON_CLR[s.person] || "bg-gray-100"}`}>{s.person}</span>
              <span className="ml-auto text-xs text-gray-500">{s.done} / {s.total} done</span>
            </div>
            <div className="mt-3 h-2 bg-gray-100 rounded-sm overflow-hidden">
              <div className={`h-full ${s.pct === 100 ? "bg-[#15803D]" : "bg-black"}`} style={{ width: `${s.pct}%` }} />
            </div>
            <div className="data-num text-2xl mt-2 font-black">{s.pct}%</div>
          </div>
        ))}
      </div>

      {/* Per-person columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {loading && <div className="lg:col-span-3 card-flat p-8 text-center text-gray-400">Loading…</div>}
        {!loading && Object.entries(data?.by_person || {}).map(([person, tasks]) => (
          <div key={person} className="card-flat" data-testid={`col-${person}`}>
            <div className="p-4 border-b flex items-center gap-2">
              <User size={16} className="text-gray-400" />
              <div className="font-heading font-black text-lg">{person}</div>
              <span className="text-xs text-gray-500 ml-auto">{tasks.filter((t) => t.done).length}/{tasks.length}</span>
            </div>
            <ul className="divide-y divide-gray-200">
              {tasks.map((t) => (
                <li key={t.id} className={`p-3 flex items-start gap-3 hover:bg-gray-50 ${t.done ? "bg-[#F0FDF4]" : ""}`} data-testid={`task-${t.id}`}>
                  <button onClick={() => toggle(t)} className="mt-0.5 shrink-0" data-testid={`toggle-${t.id}`}>
                    {t.done
                      ? <CheckSquare size={20} className="text-[#15803D]" strokeWidth={2.5} />
                      : <Square size={20} className="text-gray-300" strokeWidth={2} />
                    }
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${t.done ? "line-through text-gray-500" : "font-medium"}`}>{t.task}</div>
                    {t.done && t.done_by && (
                      <div className="text-[10px] text-gray-400 mt-0.5">✓ by {t.done_by}{t.done_at ? ` · ${new Date(t.done_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : ""}</div>
                    )}
                  </div>
                  {isAdmin && (
                    <button onClick={() => del(t.id)} className="text-gray-300 hover:text-[#B91C1C] shrink-0" title="Remove task" data-testid={`del-${t.id}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {isAdmin && (
              <div className="p-3 border-t">
                {addForPerson === person ? (
                  <div className="flex gap-2">
                    <input className="input-lg h-9 flex-1" placeholder="New task…" value={newTask}
                      onChange={(e) => setNewTask(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addTask()}
                      data-testid={`newtask-${person}`} autoFocus />
                    <button onClick={addTask} className="btn-primary h-9 px-3 flex items-center gap-1"><Save size={13} /></button>
                    <button onClick={() => { setAddForPerson(null); setNewTask(""); }} className="h-9 w-9 border border-gray-300 rounded-sm flex items-center justify-center"><X size={13} /></button>
                  </div>
                ) : (
                  <button onClick={() => setAddForPerson(person)}
                    className="text-xs font-semibold text-gray-500 hover:text-black flex items-center gap-1"
                    data-testid={`add-btn-${person}`}>
                    <Plus size={13} /> Add task
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <PendingWorks currentUser={user} />

      <p className="text-xs text-gray-500">
        Checkmarks are per-date. Change the date at top to review any day&apos;s completion history.
      </p>
    </div>
  );
}
